import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { PartnerSchedulingClientStatus } from '../entities/PartnerSchedulingClientStatus.js';
import { normalizeSchedulingKey } from '../ticketing/partner-scheduling-patterns.js';
import type { PartnerSchedulingClientRecordInput } from './partner-import-types.js';

/** Rebuilds client status rows from workbook import, preserving existing enabled flags. */
export async function syncPartnerSchedulingClientStatus(
  clients: PartnerSchedulingClientRecordInput[],
): Promise<void> {
  const collection = await getRawCollection<EntityData<PartnerSchedulingClientStatus>>(
    PartnerSchedulingClientStatus._collectionName,
  );

  const uniqueByKey = new Map<string, string>();
  for (const client of clients) {
    const clientKey = normalizeSchedulingKey(client.clientKey || client.clientName);
    if (!clientKey) continue;
    const displayName = client.clientName?.trim() || client.clientKey?.trim() || clientKey;
    uniqueByKey.set(clientKey, displayName);
  }

  const existingRows = await collection.find({}).toArray();
  const enabledByKey = new Map(
    existingRows.map(row => [normalizeSchedulingKey(row.clientKey ?? ''), row.enabled === true]),
  );

  const workbookKeys = [...uniqueByKey.keys()];
  if (workbookKeys.length === 0) {
    await collection.deleteMany({});
    return;
  }

  await collection.deleteMany({ clientKey: { $nin: workbookKeys } });

  await collection.bulkWrite(
    [...uniqueByKey.entries()].map(([clientKey, clientName]) => ({
      replaceOne: {
        filter: { clientKey },
        replacement: {
          _id: clientKey,
          _class: PartnerSchedulingClientStatus._class,
          clientKey,
          clientName,
          enabled: enabledByKey.has(clientKey) ? enabledByKey.get(clientKey)! : false,
        },
        upsert: true,
      },
    })),
  );
}
